/**
 * Global Settings Manager
 * Handles persistence, defaults, and real-time application
 */

export class SettingsManager {
  constructor() {
    this.storageKey = 'destiny-reborn-settings';
    this.inMemoryFallback = {};
    this.listeners = new Set();
    
    // Default settings
    this.defaults = {
      audioEnabled: true,
      theme: 'system', // 'light', 'dark', 'system'
      language: 'es', // 'es', 'en'
      textLarge: false,
      contrastHigh: false
    };
    
    this.currentSettings = this.load();
  }

  // Load settings from localStorage with fallback
  load() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all keys exist
        return { ...this.defaults, ...parsed };
      }
    } catch (error) {
      console.warn('Failed to load settings from localStorage:', error);
    }
    
    // Return defaults if loading failed
    return { ...this.defaults };
  }

  // Save settings to localStorage with fallback
  save(settings) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save settings to localStorage, using memory fallback:', error);
      this.inMemoryFallback = { ...settings };
    }
  }

  // Get a specific setting
  get(key) {
    return this.currentSettings[key] ?? this.defaults[key];
  }

  // Set a specific setting
  set(key, value) {
    const oldValue = this.currentSettings[key];
    this.currentSettings[key] = value;
    this.save(this.currentSettings);
    
    // Notify listeners
    this.notifyListeners(key, value, oldValue);
    
    // Log telemetry in dev
    if (window.__telemetry) {
      window.__telemetry.log('settings_change', { 
        key, 
        value, 
        oldValue,
        timestamp: Date.now()
      });
    }
    
    return value;
  }

  // Get all settings
  getAll() {
    return { ...this.currentSettings };
  }

  // Set multiple settings at once
  setMultiple(newSettings) {
    const changes = {};
    
    for (const [key, value] of Object.entries(newSettings)) {
      const oldValue = this.currentSettings[key];
      if (oldValue !== value) {
        changes[key] = { old: oldValue, new: value };
        this.currentSettings[key] = value;
      }
    }
    
    if (Object.keys(changes).length > 0) {
      this.save(this.currentSettings);
      
      // Notify listeners for each change
      for (const [key, change] of Object.entries(changes)) {
        this.notifyListeners(key, change.new, change.old);
      }
      
      // Log batch telemetry in dev
      if (window.__telemetry) {
        window.__telemetry.log('settings_batch_change', { 
          changes,
          timestamp: Date.now()
        });
      }
    }
    
    return this.currentSettings;
  }

  // Reset to defaults
  reset() {
    const oldSettings = { ...this.currentSettings };
    this.currentSettings = { ...this.defaults };
    this.save(this.currentSettings);
    
    // Notify listeners for all changes
    for (const [key, value] of Object.entries(this.currentSettings)) {
      if (oldSettings[key] !== value) {
        this.notifyListeners(key, value, oldSettings[key]);
      }
    }
    
    if (window.__telemetry) {
      window.__telemetry.log('settings_reset', { 
        oldSettings,
        newSettings: this.currentSettings,
        timestamp: Date.now()
      });
    }
    
    return this.currentSettings;
  }

  // Subscribe to setting changes
  subscribe(callback) {
    this.listeners.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback);
    };
  }

  // Notify all listeners
  notifyListeners(key, newValue, oldValue) {
    for (const callback of this.listeners) {
      try {
        callback(key, newValue, oldValue);
      } catch (error) {
        console.error('Settings listener error:', error);
      }
    }
  }

  // Convenience methods for common settings
  isAudioEnabled() {
    return this.get('audioEnabled');
  }

  getTheme() {
    return this.get('theme');
  }

  getLanguage() {
    return this.get('language');
  }

  isTextLarge() {
    return this.get('textLarge');
  }

  isContrastHigh() {
    return this.get('contrastHigh');
  }

  // Apply theme immediately
  applyTheme() {
    const theme = this.getTheme();
    const body = document.body;
    
    // Remove existing theme classes
    body.classList.remove('theme-light', 'theme-dark');
    
    if (theme === 'system') {
      // Use system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      body.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
    } else {
      body.classList.add(`theme-${theme}`);
    }
  }

  // Apply accessibility settings immediately
  applyAccessibility() {
    const body = document.body;
    
    // Text size
    if (this.isTextLarge()) {
      body.classList.add('text-lg');
    } else {
      body.classList.remove('text-lg');
    }
    
    // High contrast
    if (this.isContrastHigh()) {
      body.classList.add('contrast-high');
    } else {
      body.classList.remove('contrast-high');
    }
  }

  // Apply all visual settings immediately
  applyAll() {
    this.applyTheme();
    this.applyAccessibility();
  }

  // Initialize and apply settings on page load
  init() {
    // Apply initial settings
    this.applyAll();
    
    // Listen for system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleSystemThemeChange = () => {
        if (this.getTheme() === 'system') {
          this.applyTheme();
        }
      };
      
      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleSystemThemeChange);
      } else {
        // Legacy browsers
        mediaQuery.addListener(handleSystemThemeChange);
      }
    }
    
    // Subscribe to our own changes to apply them
    this.subscribe((key, newValue) => {
      switch (key) {
        case 'theme':
          this.applyTheme();
          break;
        case 'textLarge':
        case 'contrastHigh':
          this.applyAccessibility();
          break;
      }
    });
    
    console.log('Settings manager initialized:', this.currentSettings);
  }
}

// Singleton instance
export const settings = new SettingsManager();